// src/modules/app-views/products/pages/Document/tabs/FiscalTab/FiscalDataCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Card, Col, Row, Form, Divider } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleSelectAsyncNcm from 'components/SimpleComponents/SimpleSelectAsyncNcm';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { ICMS_ORIGIN_OPTIONS, CFOP_OPTIONS } from 'modules/app-views/products/constants';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalDataCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext() || {};

  // Watch hasSt and hasTaxBenefit to conditionally show related fields
  const hasSt = Form.useWatch(['fiscalData', 'hasSt'], form);
  const hasTaxBenefit = Form.useWatch(['fiscalData', 'hasTaxBenefit'], form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={<div className="text-muted">{translateX('fiscal_codes')}</div>}>
      {/* Row 1: NCM, CEST, CFOP, Origin */}
      <Row gutter={ROW_GUTTER}>
        {/* NCM - Nomenclatura Comum do Mercosul (8 digits) */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelectAsyncNcm
            name="fiscalData.ncm"
            label="ncm"
            placeholder="search_ncm"
          />
        </Col>

        {/* CFOP - Código Fiscal de Operações e Prestações (suggested for this product) */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name="fiscalData.cfop"
            label="cfop_suggested"
            options={CFOP_OPTIONS}
            showSearch
            allowClear
            tooltip="cfop_product_tooltip"
          />
        </Col>

        {/* ICMS Origin - Required for product (0-8) */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name="fiscalData.icms.origin"
            label="icms_origin"
            options={ICMS_ORIGIN_OPTIONS}
            allowClear
            tooltip="icms_origin_tooltip"
          />
        </Col>

        {/* CEST - Código Especificador da Substituição Tributária (7 digits) */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleInput
            name="fiscalData.cest"
            label="cest"
            placeholder="0000000"
            maxLength={7}
            tooltip="cest_tooltip"
            required={hasSt}
            rules={[{ pattern: /^\d{7}$/, message: translateX('cest_invalid_format') }]}
          />
        </Col>
      </Row>

      <Divider className="my-3" />

      {/* Row 2: Fiscal Indicators */}
      <Row gutter={ROW_GUTTER}>
        {/* Has ST - Substituição Tributária */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSwitch
            name="fiscalData.hasSt"
            label="has_st"
            tooltip="has_st_tooltip"
          />
        </Col>

        {/* Has Tax Benefit */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSwitch
            name="fiscalData.hasTaxBenefit"
            label="has_tax_benefit"
            tooltip="has_tax_benefit_tooltip"
          />
        </Col>

        {/* Benefit Code - Only shown when hasTaxBenefit is true */}
        {hasTaxBenefit && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleInput
              name="fiscalData.benefitCode"
              label="benefit_code"
              placeholder="XX000000"
              tooltip="benefit_code_tooltip"
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalDataCard;
